import { useEffect, useMemo, useState } from 'react';

import axios, { AxiosResponse } from 'axios';
import { isEqual } from 'lodash';

import { useAxiosRequest } from '@admin/hooks';

import {
  DispatcherData,
  DispatcherDispatchRecord,
  DispatcherFilters,
  DispatcherOrderRecord,
  FieldShiftAssignmentRecord,
} from '../dispatcher_types';

export type {
  DispatcherData,
  DispatcherDispatchRecord,
  DispatcherFilters,
  DispatcherOrderRecord,
} from '../dispatcher_types';

const cleanupFilters = (filters: DispatcherFilters): Record<string, string | number> => {
  const results: Record<string, string | number> = {};

  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      results[key] = value;
    }
  });

  return results;
};

export const useDispatcherData = (filters: DispatcherFilters) => {
  const filtering = !!filters.region_id && !!filters.date;
  const params = useMemo(() => cleanupFilters(filters), [filters]);
  const [refreshNonce, setRefreshNonce] = useState(0);
  const [requestParams, setRequestParams] = useState<Record<string, string | number> | null>(null);

  const [dispatchesRequest, setDispatchesRequest] = useState<
    Promise<AxiosResponse<DispatcherDispatchRecord[]>> | undefined
  >();
  const [ordersRequest, setOrdersRequest] = useState<Promise<AxiosResponse<DispatcherOrderRecord[]>> | undefined>();
  const [fieldShiftAssignmentsRequest, setFieldShiftAssignmentsRequest] = useState<
    Promise<AxiosResponse<FieldShiftAssignmentRecord[]>> | undefined
  >();

  useEffect(() => {
    if (!filtering) {
      setRequestParams(null);
      setDispatchesRequest(undefined);
      setOrdersRequest(undefined);
      setFieldShiftAssignmentsRequest(undefined);
      return;
    }

    setRequestParams(params);
    setDispatchesRequest(axios.get<DispatcherDispatchRecord[]>('/dispatches.json', { params }));
    setOrdersRequest(axios.get<DispatcherOrderRecord[]>('/dispatching/orders.json', { params }));
    setFieldShiftAssignmentsRequest(
      axios.get<FieldShiftAssignmentRecord[]>('/dispatching/field_shift_assignments.json', { params }),
    );
  }, [filtering, params, refreshNonce]);

  const {
    loading: dispatchesLoading,
    response: dispatchesResponse,
    error: dispatchesError,
  } = useAxiosRequest(dispatchesRequest);
  const { loading: ordersLoading, response: ordersResponse, error: ordersError } = useAxiosRequest(ordersRequest);
  const {
    loading: fieldShiftAssignmentsLoading,
    response: fieldShiftAssignmentsResponse,
    error: fieldShiftAssignmentsError,
  } = useAxiosRequest(fieldShiftAssignmentsRequest);

  const data = useMemo((): DispatcherData | undefined => {
    if (!dispatchesResponse || !ordersResponse || !fieldShiftAssignmentsResponse) {
      return undefined;
    }

    const dispatches = dispatchesResponse.data
      .sort((a, b) => (a.id ?? 0) - (b.id ?? 0))
      .map((dispatch) => ({ ...dispatch, mapID: dispatch.id }));

    const orders = ordersResponse.data;
    const nonRestockChildOrders = orders.filter((order) => order.parent_id && order.type !== 'restock');

    return {
      dispatches,
      orders,
      fieldShiftAssignments: fieldShiftAssignmentsResponse.data,
      nonRestockChildOrders,
    };
  }, [dispatchesResponse, fieldShiftAssignmentsResponse, ordersResponse]);

  const awaitingFreshRequests = filtering && (requestParams === null || !isEqual(requestParams, params));
  const loading =
    filtering && (awaitingFreshRequests || dispatchesLoading || ordersLoading || fieldShiftAssignmentsLoading);
  const error = dispatchesError ?? ordersError ?? fieldShiftAssignmentsError;

  return {
    data,
    loading,
    error,
    filtering,
    reload: () => setRefreshNonce((nonce) => nonce + 1),
  };
};
